import type {
  MindstringPrototype,
  SelectedProfileInput,
  SelectedProfileOutput,
  SelectedSituation,
  SelectedThought,
  SelectedThoughtFree,
  ThoughtGrouping,
} from "@/generated/fastapi";
import type { ActiveStudioContentType } from "@/shared/types";

export type SelectedProfileShape = SelectedProfileInput | SelectedProfileOutput;

export type SelectedProfileUiState = {
  activeStudioContent?: ActiveStudioContentType;
  activeStudioGrouping?: ThoughtGrouping;
  activePrototypeCategory?: MindstringPrototype;
  manualRerank?: boolean;
};

function cloneSelectedSituations(
  selectedSituations: SelectedSituation[],
): SelectedSituation[] {
  return selectedSituations.map((selection) => ({
    prototype: selection.prototype,
    situations_ids: [...selection.situations_ids],
  }));
}

function cloneSelectedThoughts(
  selectedThoughts: SelectedThought[],
): SelectedThought[] {
  return selectedThoughts.map((selection) => ({
    prototype: selection.prototype,
    situation_id: selection.situation_id ?? null,
    thoughts_ids: [...selection.thoughts_ids],
  }));
}

export function buildSelectedThoughtsFree(
  selectedThoughts: SelectedThought[],
): SelectedThoughtFree[] {
  const prototypeOrder: SelectedThoughtFree["prototype"][] = [];
  const thoughtIdsByPrototype = new Map<
    SelectedThoughtFree["prototype"],
    string[]
  >();
  const seenThoughtIdsByPrototype = new Map<
    SelectedThoughtFree["prototype"],
    Set<string>
  >();

  selectedThoughts.forEach((selection) => {
    if (!thoughtIdsByPrototype.has(selection.prototype)) {
      prototypeOrder.push(selection.prototype);
      thoughtIdsByPrototype.set(selection.prototype, []);
      seenThoughtIdsByPrototype.set(selection.prototype, new Set());
    }

    const orderedThoughtIds = thoughtIdsByPrototype.get(selection.prototype);
    const seenThoughtIds = seenThoughtIdsByPrototype.get(selection.prototype);
    if (!orderedThoughtIds || !seenThoughtIds) {
      return;
    }

    selection.thoughts_ids.forEach((thoughtId) => {
      if (seenThoughtIds.has(thoughtId)) {
        return;
      }

      orderedThoughtIds.push(thoughtId);
      seenThoughtIds.add(thoughtId);
    });
  });

  return prototypeOrder
    .map((prototype) => ({
      prototype,
      thoughts_ids: thoughtIdsByPrototype.get(prototype) ?? [],
    }))
    .filter((selection) => selection.thoughts_ids.length > 0);
}

export function buildSelectedProfile({
  selectedSituations,
  selectedThoughts,
  selectedThoughtsFree,
  selectedSuggestedThoughts,
  uiState,
  includeFreeListOrder = false,
}: {
  selectedSituations: SelectedSituation[];
  selectedThoughts: SelectedThought[];
  selectedThoughtsFree?: SelectedThoughtFree[];
  selectedSuggestedThoughts?: string[];
  uiState?: SelectedProfileUiState;
  includeFreeListOrder?: boolean;
}): SelectedProfileInput {
  const selectedProfile: SelectedProfileInput = {};

  if (selectedSituations.length > 0) {
    selectedProfile.selected_situations =
      cloneSelectedSituations(selectedSituations);
  }

  if (selectedThoughts.length > 0) {
    selectedProfile.selected_thoughts = cloneSelectedThoughts(selectedThoughts);
  }

  if (includeFreeListOrder) {
    const explicitSelectedThoughtsFree = selectedThoughtsFree?.map(
      (selection) => ({
        prototype: selection.prototype,
        thoughts_ids: [...selection.thoughts_ids],
      }),
    );
    const derivedSelectedThoughtsFree =
      buildSelectedThoughtsFree(selectedThoughts);
    const nextSelectedThoughtsFree =
      explicitSelectedThoughtsFree && explicitSelectedThoughtsFree.length > 0
        ? explicitSelectedThoughtsFree
        : derivedSelectedThoughtsFree;

    if (nextSelectedThoughtsFree.length > 0) {
      selectedProfile.selected_thoughts_free = nextSelectedThoughtsFree;
    }
  }

  if (selectedSuggestedThoughts && selectedSuggestedThoughts.length > 0) {
    selectedProfile.selected_suggested_thoughts = [
      ...selectedSuggestedThoughts,
    ];
  }

  if (uiState?.activeStudioContent) {
    selectedProfile.active_studio_content = uiState.activeStudioContent;
  }

  if (uiState?.activeStudioGrouping) {
    selectedProfile.active_studio_grouping = uiState.activeStudioGrouping;
  }

  if (uiState?.activePrototypeCategory) {
    selectedProfile.active_prototype_category = uiState.activePrototypeCategory;
  }

  if (typeof uiState?.manualRerank === "boolean") {
    selectedProfile.manual_rerank = uiState.manualRerank;
  }

  return selectedProfile;
}

export function sanitizeSelectedProfile({
  selectedProfile,
  existingSituationIds,
}: {
  selectedProfile: SelectedProfileShape;
  existingSituationIds?: string[];
}): SelectedProfileInput {
  const validSituationIds = existingSituationIds
    ? new Set(existingSituationIds)
    : null;

  const sanitizedSituations = (selectedProfile.selected_situations || [])
    .map((selection) => ({
      prototype: selection.prototype,
      situations_ids: validSituationIds
        ? selection.situations_ids.filter((id) => validSituationIds.has(id))
        : selection.situations_ids,
    }))
    .filter((selection) => selection.situations_ids.length > 0);

  const sanitizedThoughts = (selectedProfile.selected_thoughts || [])
    .filter((selection) =>
      validSituationIds
        ? !selection.situation_id ||
          validSituationIds.has(selection.situation_id)
        : true,
    )
    .map((selection) => ({
      prototype: selection.prototype,
      situation_id: selection.situation_id ?? null,
      thoughts_ids: Array.from(new Set(selection.thoughts_ids || [])),
    }))
    .filter((selection) => selection.thoughts_ids.length > 0);

  const sanitizedThoughtsFree = (
    selectedProfile.selected_thoughts_free &&
    selectedProfile.selected_thoughts_free.length > 0
      ? selectedProfile.selected_thoughts_free
      : buildSelectedThoughtsFree(sanitizedThoughts)
  )
    .map((selection) => ({
      prototype: selection.prototype,
      thoughts_ids: Array.from(new Set(selection.thoughts_ids || [])),
    }))
    .filter((selection) => selection.thoughts_ids.length > 0);

  const sanitizedSelectedProfile: SelectedProfileInput = {};

  if (sanitizedSituations.length > 0) {
    sanitizedSelectedProfile.selected_situations = sanitizedSituations;
  }

  if (sanitizedThoughts.length > 0) {
    sanitizedSelectedProfile.selected_thoughts = sanitizedThoughts;
  }

  if (sanitizedThoughtsFree.length > 0) {
    sanitizedSelectedProfile.selected_thoughts_free = sanitizedThoughtsFree;
  }

  if (
    selectedProfile.selected_suggested_thoughts &&
    selectedProfile.selected_suggested_thoughts.length > 0
  ) {
    sanitizedSelectedProfile.selected_suggested_thoughts = Array.from(
      new Set(selectedProfile.selected_suggested_thoughts),
    );
  }

  if (selectedProfile.active_studio_content) {
    sanitizedSelectedProfile.active_studio_content =
      selectedProfile.active_studio_content;
  }

  if (selectedProfile.active_studio_grouping) {
    sanitizedSelectedProfile.active_studio_grouping =
      selectedProfile.active_studio_grouping;
  }

  if (selectedProfile.active_prototype_category) {
    sanitizedSelectedProfile.active_prototype_category =
      selectedProfile.active_prototype_category;
  }

  if (typeof selectedProfile.manual_rerank === "boolean") {
    sanitizedSelectedProfile.manual_rerank = selectedProfile.manual_rerank;
  }

  return sanitizedSelectedProfile;
}

export function countSelectedProfileThoughts(
  selectedProfile: SelectedProfileShape,
): number {
  const selectedSuggestedThoughtsCount = new Set(
    selectedProfile.selected_suggested_thoughts || [],
  ).size;

  if (
    selectedProfile.selected_thoughts_free &&
    selectedProfile.selected_thoughts_free.length > 0
  ) {
    return selectedProfile.selected_thoughts_free.reduce(
      (count, selection) => count + new Set(selection.thoughts_ids || []).size,
      selectedSuggestedThoughtsCount,
    );
  }

  return (selectedProfile.selected_thoughts || []).reduce(
    (count, selection) => count + new Set(selection.thoughts_ids || []).size,
    selectedSuggestedThoughtsCount,
  );
}
